import { Badge, Button, Card, Col, ListGroupItem, OverlayTrigger, Placeholder, Row, Tooltip } from "react-bootstrap";
import { CompassParticipant, PatientVisit, PatientVisitMetadata } from '../../amplify/appsync/search-patients';

import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons";
import { faBed, faChevronDown, faChevronUp, faCreditCard, faExclamationTriangle, faGear, faPerson, faSpinner, faStar, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { callQuery, Favorite, Note, NOTES_FOR_USERNAME_AND_MRN } from "../../amplify/appsync/query";
import { callWrite, INSERT_NOTE } from "../../amplify/appsync/write";
import { generateDates, getDaysBetweenDates, getDayTruncatedDate, getUserFriendlyDate, withTimezone } from "../../utils/time";
import { ExpandingTextarea } from "../input/ExpandingTextArea";


export interface CompassItemProps {
    patient: CompassParticipant & PatientVisit & PatientVisitMetadata,
    pastFavorites?: Favorite[],
    pastReviewed?: Favorite[],
    updatePatientWithNote: any,
    csnToFavorites: any,
    columns: any[],
    setShowContextMenu: any,
    setContextMenu: any,
    tenant: string,
    padNotes?: boolean,
    isPlaceholder?: boolean,
}

export const CompassItem = (props: CompassItemProps) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
    const [noteText, setNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [saveTimeout, setSaveTimeout] = useState<any>(null);
    const [lastSavedNoteText, setLastSavedNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [isNoteTextChanged, setIsNoteTextChanged] = useState(false);
    const [events, setEvents] = useState<any[]>([]);
    const [userFriendlyDateFormat, setUserFriendlyDateFormat] = useState<boolean>(true);
    const [pastNotes, setPastNotes] = useState<Note[]>([]);

    const totalRelativeCols = props.columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);

    // Effect to handle autosave on content change
    useEffect(() => {
        // Clear any existing timeout if content changes
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Set a new timeout to save the content after 2 seconds of inactivity
        const newTimeout = setTimeout(() => {
            if (isNoteTextChanged) {
                saveNote();
            }
        }, 2000);

        // Store the timeout ID so it can be cleared later
        setSaveTimeout(newTimeout);

        // Clean up the timeout when the component unmounts or content changes
        return () => {
            clearTimeout(newTimeout);
        };
    }, [noteText]); // Only trigger effect when content changes

    useEffect(() => {
        if (isExpanded) {
            callQuery({
                operation: NOTES_FOR_USERNAME_AND_MRN,
                mrn: props.patient.mrn,
            })
            .then((notes: Note[]) => {
                const filteredNotes = notes.filter(note => note.csn !== props.patient.csn);
                setPastNotes(filteredNotes);
            });
        }
    }, [isExpanded]);

    const isFavorite = () => {
        if (props.csnToFavorites[props.patient.csn] && props.csnToFavorites[props.patient.csn].length > 0) {
            return true;
        }
        return false;
    }

    const handleNoteChange = (text: any) => {
        setIsNoteTextChanged(true);
        setNoteText(text);
    };

    const saveNote = () => {
        if (lastSavedNoteText !== noteText) {
            setIsSavingNote(true);
            callWrite({
                operation: INSERT_NOTE,
                creation_time: new Date().getTime(),
                note_type: "note",
                csn: props.patient.csn,
                mrn: props.patient.mrn,
                note: noteText,
            })
            .then((output: Note[]) => {
                console.log("Saved note", output);
                setIsSavingNote(false);
                setNoteText(output[0].note);
                props.updatePatientWithNote(output[0]);
                setLastSavedNoteText(output[0].note);
            });
        }
    }

    const getWidth = (col: any) => {
        return `${100 * col.size / totalRelativeCols}%`;
    }

    const getNumDaysInStudy = useMemo(() => {
        if (props.patient && props.patient.enrollment_date && props.patient.end_date) {
            return generateDates(getDayTruncatedDate(withTimezone(new Date(props.patient.enrollment_date), 'America/Vancouver')), getDayTruncatedDate(withTimezone(new Date(props.patient.end_date), 'America/Vancouver'))).length;
        } else if (props.patient && props.patient.enrollment_date) {
            return generateDates(getDayTruncatedDate(withTimezone(new Date(props.patient.enrollment_date), 'America/Vancouver')), getDayTruncatedDate(withTimezone(new Date(), 'America/Vancouver'))).length;
        } else {
            return 0;
        }
    }, [props.patient]);

    const getFriendlyTimeFormat = (col: any) => {
        // @ts-ignore
        const hours = parseInt((new Date() - new Date(props.patient[col.id])) / (1000 * 60 * 60), 10);
        if (hours >= 24) {
            return `${Math.round(hours / 24)} days ago`;
        } else {
            return `${hours} hours ago`
        }
    }

    return (
        <ListGroupItem className="d-flex flex-column trackboard-item" style={{backgroundColor: props.patient && props.patient.withdrawn === '1' ? '#EFEFEF' : ''}}>
            <div style={{ display: 'block', width: '100%' }} onClick={() => window.open(`/compass/${props.patient.study_id}`, "_blank")}>
                {
                    props.columns.map((col: any, idx: number) => {
                        if (props.isPlaceholder) {
                            return (
                                <div key={`placeholder-${idx}`} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={6} size="xs" />
                                    </Placeholder>
                                </div>
                            )
                        }
                        if (col.id === "enrollment_date" || col.id === "end_date") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {
                                        // @ts-ignore
                                        props.patient[col.id] ? (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip>
                                                        {/* @ts-ignore */}
                                                        {new Date(props.patient[col.id]).toLocaleDateString()}
                                                    </Tooltip>
                                                }
                                            >
                                                <span>
                                                    {
                                                        getFriendlyTimeFormat(col)
                                                    }
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <>-</>
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "last_monitor_data" || col.id === "last_survey_data") {
                            let isDelayed = false;
                            // @ts-ignore
                            if (props.patient && props.patient[col.id] && getNumDaysInStudy > 0) {
                                // @ts-ignore
                                const delayedSec = (new Date().getTime() - new Date(props.patient[col.id]).getTime()) / 1000;
                                isDelayed = delayedSec > (24 * 60 * 60);
                            // @ts-ignore
                            } else if (props.patient && !props.patient[col.id]) {
                                isDelayed = true;
                            }
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isDelayed ? 'red' : '' }}>
                                    {
                                        // @ts-ignore
                                        props.patient[col.id] ? (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip>
                                                        {/* @ts-ignore */}
                                                        {new Date(props.patient[col.id]).toLocaleDateString()}
                                                    </Tooltip>
                                                }
                                            >
                                                <span>
                                                    {
                                                        getFriendlyTimeFormat(col)
                                                    }
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <>-</>
                                        )
                                    }
                                    {
                                        isDelayed && (
                                            <>{' '}<FontAwesomeIcon icon={faExclamationTriangle} /></>
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "epic_revisit_score") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {props.patient.epic_revisit_score && props.patient.epic_revisit_score.length > 0 ?  Number(props.patient.epic_revisit_score[0]).toFixed(2) : ""}{' '}
                                </div>
                            )
                        } else if (col.id === "monitoring") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {
                                        props.patient && props.patient.withdrawn === '1' ? (
                                            <Badge bg='danger'>Withdrawn</Badge>
                                        ) : props.patient && props.patient.earliest_revisit_time && getDaysBetweenDates(props.patient.enrollment_date, props.patient.earliest_revisit_time) <= 30 ? (
                                            <Badge bg='success'>Completed - Revisit</Badge>
                                        ) : props.patient && getDaysBetweenDates(props.patient.enrollment_date, new Date()) > 30 ? (
                                            <Badge bg='secondary'>Completed - 30 Days</Badge>
                                        ) : (
                                            <Badge bg='warning'>In-Progress</Badge>
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "monitor_counts") {
                            let numDaysPreMonitor = 0;
                            if (props.patient.monitor_counts !== undefined && props.patient.monitor_counts !== null) {
                                const differenceInMilliseconds = getDayTruncatedDate(withTimezone(new Date(props.patient.enrollment_date), 'America/Vancouver')).getTime() - new Date(props.patient.monitor_start).getTime();
                                const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
                                numDaysPreMonitor = Math.max(Math.round(differenceInDays), 0);
                            }

                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {props.patient.monitor_counts !== undefined && props.patient.monitor_counts !== null ? (
                                        <>
                                            {Number(props.patient.monitor_counts) - numDaysPreMonitor}/{getNumDaysInStudy}
                                        </>
                                    ) : (<>-</>)}
                                </div>
                            )
                        } else if (col.id === "survey_counts") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {props.patient.survey_counts !== undefined && props.patient.survey_counts !== null ? (
                                        <>
                                            {props.patient.survey_counts}/{getNumDaysInStudy}
                                        </>
                                    ) : (<>-</>)}
                                </div>
                            )
                        } else if (col.id === "action") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {isLoadingFavorite} {isFavorite()}
                                    <Button
                                        variant="primary"
                                        className={"trackboard-save-btn"}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            props.setShowContextMenu(true);
                                            props.setContextMenu({
                                                x: e.pageX,
                                                y: e.pageY,
                                                patient: props.patient,
                                                listName: null,
                                            })
                                        }}
                                    >
                                        {
                                            isLoadingFavorite ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : isFavorite() ? (
                                                <>
                                                    <FontAwesomeIcon icon={faStar} />
                                                </>
                                            ) : (
                                                <>
                                                    {/** @ts-ignore */}
                                                    <FontAwesomeIcon icon={faStarOutline} />
                                                </>
                                            )
                                        }
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="trackboard-expand-btn"
                                        onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                                    >
                                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                    </Button>
                                </div>
                            )
                        } else {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {/* @ts-ignore */}
                                    {props.patient[col.id] ? props.patient[col.id] === "NaN" ? "-" : props.patient[col.id] : "-"}
                                </div>
                            )
                        }
                    })
                }
            </div>
            {
                !isExpanded && noteText && (
                    <div style={{ display: 'block', width: '100%', cursor: 'default' }}>
                        <div style={{display: 'inline-block'}}>
                            <div style={{ paddingLeft: props.padNotes ? '32px' : '0px', display: 'inline-block', color: '#999' }}>
                                {/** @ts-ignore */}
                                {noteText}
                            </div>
                        </div>
                    </div>
                )
            }

            {isExpanded && !props.isPlaceholder &&
                <div style={{ cursor: 'default', display: 'block', width: '100%', paddingTop: '12px', }}>

                    <Row>
                        <Col md={4} style={{paddingLeft: '4px', paddingRight: '0px'}}>
                            <div>
                                <FontAwesomeIcon icon={faGear} fixedWidth />{' '}Serial Number: <strong>{props.patient.serial_number}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Participant ID: <strong>{props.patient.participant_id}</strong>
                            </div>
                        </Col>
                        <Col md={4} style={{paddingRight: '0px'}}>
                            <div>
                                <FontAwesomeIcon icon={faBed} fixedWidth />{' '}CSN: <strong>{props.patient.csn}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faUser} fixedWidth />{' '}Internal ID: <a href={`/dashboard/${props.patient.internal_csn}`} target="_blank"><strong>{props.patient.internal_csn}</strong></a>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faUser} fixedWidth />{' '}MRN: <strong>{props.patient.mrn}</strong>
                            </div>
                        </Col>
                        <Col md={4} style={{paddingRight: '0px'}}>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Ethnicity: <strong>{props.patient.ethnicity ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Race: <strong>{props.patient.race ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faCreditCard} fixedWidth />{' '}Payor: <strong>{props.patient.payor_class ?? "-"}</strong>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{paddingRight: '4px'}}>
                            <Card className="trackboard-notes-container">
                                <Card.Body>
                                    <ExpandingTextarea setText={handleNoteChange} text={noteText} sendChat={() => { } } placeholder={"Add your notes for this patient visit..."} isDisabled={false} />
                                    <Row style={{position: 'absolute', bottom: '12px', right: '12px'}}>
                                        <Col className="text-right">
                                            {
                                                isSavingNote ? (
                                                    <><FontAwesomeIcon icon={faSpinner} spin />{' '}Saving...</>
                                                ) : noteText === lastSavedNoteText ? (
                                                    <>Saved</>
                                                ) : (
                                                    <>Not Saved</>
                                                )
                                            }
                                        </Col>
                                    </Row>

                                    {
                                        pastNotes && pastNotes.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={12} style={{paddingRight: '4px'}}>
                                                        {
                                                            pastNotes.map((note) => (
                                                                <>
                                                                    <hr />
                                                                    <Row>
                                                                        <Col style={{color: '#999999'}}>
                                                                            <strong>Previous Note</strong> &bull; CSN {note.csn} &bull; Last updated at: {getUserFriendlyDate(note.creation_time)}
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col>
                                                                            {note.note}
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            ))
                                                        }
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            }
        </ListGroupItem>
    );
};
